import { useState, useEffect, useRef } from "react"
import { Link } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { fetcher } from "../lib/api"
import { LIMITS, getPlan } from "../lib/plans"
import { getDefaultColor, getBadgeColors } from "../lib/colors"
import { ColorPicker } from "./color-picker"
import { Avatar } from "./avatar"
import { useTheme } from "./theme"
import { Button, Input, Label, Dialog, DialogTitle, Textarea, Select, DatePicker } from "./ui"
import { marked } from "marked"
import { useToast } from "./toast"
import { X, Paperclip, Trash2, Upload, Copy, Bell, BellOff, Lock, Archive, ArchiveRestore, Pencil, Check, Image, Palette, SmilePlus } from "lucide-react"

type MemberUser = { id: string; name: string | null; email: string; image: string | null }
type ChecklistItemType = { id: string; text: string; checked: boolean; order: number }

const formatBytes = (bytes?: number) => {
  if (!bytes || bytes === 0) return "0 B"
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i] || "B"}`
}

/** The big card detail dialog. Handles editing title, description, priority, assignee, labels, dates, checklist, comments, attachments, and subscription state. This is the most complex component in the app. */
export function CardDialog({
  open,
  onClose,
  cardId,
  boardId,
  workspaceId,
}: {
  open: boolean
  onClose: () => void
  cardId: string | null
  boardId: string
  workspaceId: string
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const commentRef = useRef<HTMLInputElement>(null)

  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [priority, setPriority] = useState("medium")
  const [assigneeIds, setAssigneeIds] = useState<string[]>([])
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false)
  const [comment, setComment] = useState("")
  const [comments, setComments] = useState<any[]>([])
  const [attachments, setAttachments] = useState<any[]>([])
  const [deletingAttachment, setDeletingAttachment] = useState<string | null>(null)
  const [labels, setLabels] = useState<string[]>([])
  const [labelColors, setLabelColors] = useState<Record<string, string>>({})
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  const [newLabel, setNewLabel] = useState("")
  const [checklistItems, setChecklistItems] = useState<ChecklistItemType[]>([])
  const [newChecklistItem, setNewChecklistItem] = useState("")
  const [startDate, setStartDate] = useState<string>("")
  const [dueDate, setDueDate] = useState<string>("")
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [isArchived, setIsArchived] = useState(false)
  const [mdPreview, setMdPreview] = useState(false)
  const [activityTab, setActivityTab] = useState<"comments" | "activity">("comments")
  const [reactionPickerFor, setReactionPickerFor] = useState<string | null>(null)
  const [activityLogs, setActivityLogs] = useState<any[]>([])
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingCommentContent, setEditingCommentContent] = useState("")
  const [coverColor, setCoverColor] = useState<string | null>(null)
  const [coverImage, setCoverImage] = useState<string | null>(null)
  const [showCoverPicker, setShowCoverPicker] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [storyPoints, setStoryPoints] = useState<number | null>(null)
  const [recurrence, setRecurrence] = useState<string>("")

  const { data: members } = useQuery({
    queryKey: ["members", workspaceId],
    queryFn: () => fetcher<{ user: MemberUser }[]>(`/api/members?workspaceId=${workspaceId}`),
    enabled: !!workspaceId,
  })

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ role: string; plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!workspaceId,
  })
  const planLimits = LIMITS[getPlan(workspace?.plan)]
  const canAttach = planLimits.attachments
  const canRecur = getPlan(workspace?.plan) !== "free"
  const memberList: MemberUser[] = members?.map((m) => m.user) || []
  const { theme } = useTheme()

  useEffect(() => {
    if (!cardId || !open) return
    setTitle("")
    setDescription("")
    setPriority("medium")
    setComments([])
    setAttachments([])
    setChecklistItems([])
    setActivityLogs([])
    setLabels([])
    setLabelColors({})
    setStartDate("")
    setDueDate("")
    setConfirmDelete(false)
    setConfirmArchive(false)
    setIsArchived(false)
    setMdPreview(false)
    setActivityTab("comments")

    fetcher<{ labelColors: Record<string, string> | null }>(`/api/boards/${boardId}`).then((b) => {
      setLabelColors(b.labelColors || {})
    }).catch(() => {})

    fetcher<any>(`/api/cards/${cardId}`).then((c) => {
      setIsArchived(!!c.archived)
      setTitle(c.title)
      setDescription(c.description || "")
      setPriority(c.priority || "medium")
      setAssigneeIds(
        c.assignees?.length ? c.assignees.map((a: any) => a.user.id) : []
      )
      setLabels(c.labels ? c.labels.split(",").map((l: string) => l.trim()).filter(Boolean) : [])
      setStartDate(c.startDate ? new Date(c.startDate).toISOString().split("T")[0] : "")
      setDueDate(c.dueDate ? new Date(c.dueDate).toISOString().split("T")[0] : "")
      setComments(c.comments || [])
      setAttachments(c.attachments || [])
      setChecklistItems(c.checklistItems || [])
      setCoverColor(c.coverColor || null)
      setCoverImage(c.coverImage || null)
      setStoryPoints(c.storyPoints ?? null)
      setRecurrence(c.recurrence || "")
    })
    fetcher<any[]>(`/api/activity?cardId=${cardId}`).then(setActivityLogs).catch(() => {})
  }, [cardId, open])

  const update = useMutation({
    mutationFn: (body: any) => fetcher(`/api/cards/${cardId}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast("Card updated", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to update card", "error"),
  })

  const updateLabelColors = useMutation({
    mutationFn: (colors: Record<string, string>) => fetcher(`/api/boards/${boardId}`, { method: "PATCH", body: JSON.stringify({ labelColors: colors }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["board", boardId] }),
    onError: (err: any) => toast(err.message || "Failed to update label colors", "error"),
  })

  const duplicateCard = useMutation({
    mutationFn: () => fetcher(`/api/cards/${cardId}/duplicate`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast("Card duplicated", "success")
      onClose()
    },
    onError: (err: any) => toast(err.message || "Failed to duplicate", "error"),
  })

  const deleteCard = useMutation({
    mutationFn: () => fetcher(`/api/cards/${cardId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast("Card deleted", "success")
      onClose()
    },
    onError: (err: any) => toast(err.message || "Failed to delete card", "error"),
  })

  const archiveCard = useMutation({
    mutationFn: () => fetcher(`/api/cards/${cardId}/archive`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast("Card archived", "success")
      onClose()
    },
    onError: (err: any) => toast(err.message || "Failed to archive card", "error"),
  })

  const unarchiveCard = useMutation({
    mutationFn: () => fetcher(`/api/cards/${cardId}/unarchive`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      queryClient.invalidateQueries({ queryKey: ["archived-cards", boardId] })
      toast("Card restored", "success")
      onClose()
    },
    onError: (err: any) => toast(err.message || "Failed to restore card", "error"),
  })

  const { data: subData, refetch: refetchSub } = useQuery({
    queryKey: ["subscriber", cardId],
    queryFn: () => fetcher<{ subscribed: boolean }>(`/api/subscribers/${cardId}`),
    enabled: !!cardId && open,
  })
  const subscribed = subData?.subscribed ?? false

  const toggleSubscribe = useMutation({
    mutationFn: () => fetcher(`/api/subscribers/${cardId}`, { method: subscribed ? "DELETE" : "POST" }),
    onSuccess: () => { refetchSub(); toast(subscribed ? "Unfollowed card" : "Following card", "success") },
    onError: (err: any) => toast(err.message || "Failed", "error"),
  })

  const editComment = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      fetcher(`/api/comments/${id}`, { method: "PATCH", body: JSON.stringify({ content }) }),
    onSuccess: (updated: any) => {
      setComments((prev) => prev.map((c) => (c.id === updated.id ? { ...c, content: updated.content } : c)))
      setEditingCommentId(null)
      toast("Comment updated", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to update comment", "error"),
  })

  const toggleReaction = useMutation({
    mutationFn: ({ commentId, emoji }: { commentId: string; emoji: string }) =>
      fetcher(`/api/reactions`, { method: "POST", body: JSON.stringify({ commentId, emoji }) }),
    onSuccess: () => {
      fetcher<any>(`/api/cards/${cardId}`).then((c) => setComments(c.comments || []))
      setReactionPickerFor(null)
    },
  })

  const addComment = useMutation({
    mutationFn: (content: string) => fetcher("/api/comments", { method: "POST", body: JSON.stringify({ cardId, content }) }),
    onSuccess: () => {
      setComment("")
      fetcher<any>(`/api/cards/${cardId}`).then((c) => setComments(c.comments || []))
      toast("Comment added", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to add comment", "error"),
  })

  const addAttachment = useMutation({
    mutationFn: async (file: File) => {
      if (!cardId) throw new Error("No card selected")
      const form = new FormData()
      form.append("cardId", cardId)
      form.append("file", file)
      return fetcher("/api/attachments/upload", { method: "POST", body: form })
    },
    onSuccess: () => {
      fetcher<any>(`/api/cards/${cardId}`).then((c) => setAttachments(c.attachments || []))
      toast("Attachment added", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to upload attachment", "error"),
  })

  const deleteAttachment = useMutation({
    mutationFn: (id: string) => fetcher(`/api/attachments/${id}`, { method: "DELETE" }),
    onMutate: (id) => setDeletingAttachment(id),
    onSuccess: (_, id) => {
      setAttachments((prev) => prev.filter((a) => a.id !== id))
      setDeletingAttachment(null)
      toast("Attachment removed", "success")
    },
    onError: (err: any) => { setDeletingAttachment(null); toast(err.message || "Failed to remove attachment", "error") },
  })

  const saveLabels = (next: string[]) => {
    setLabels(next)
    const nextColors = { ...labelColors }
    next.forEach((label, i) => {
      if (!nextColors[label]) nextColors[label] = getDefaultColor(i)
    })
    setLabelColors(nextColors)
    updateLabelColors.mutate(nextColors)
    update.mutate({ labels: next.join(",") })
  }

  const addChecklistItem = useMutation({
    mutationFn: (text: string) => fetcher("/api/checklists", { method: "POST", body: JSON.stringify({ cardId, text }) }),
    onSuccess: (item: any) => { setChecklistItems((prev) => [...prev, item]); setNewChecklistItem("") },
    onError: (err: any) => toast(err.message || "Failed to add item", "error"),
  })

  const toggleChecklistItem = useMutation({
    mutationFn: ({ id, checked }: { id: string; checked: boolean }) =>
      fetcher(`/api/checklists/${id}`, { method: "PATCH", body: JSON.stringify({ checked }) }),
    onSuccess: (item: any) => setChecklistItems((prev) => prev.map((ci) => (ci.id === item.id ? item : ci))),
    onError: (err: any) => toast(err.message || "Failed to update item", "error"),
  })

  const deleteChecklistItem = useMutation({
    mutationFn: (id: string) => fetcher(`/api/checklists/${id}`, { method: "DELETE" }),
    onSuccess: (_, id) => setChecklistItems((prev) => prev.filter((ci) => ci.id !== id)),
    onError: (err: any) => toast(err.message || "Failed to remove item", "error"),
  })

  const COVER_COLORS = ["#ef4444","#f97316","#eab308","#22c55e","#3b82f6","#8b5cf6","#ec4899","#14b8a6","#64748b","#1e293b"]

  const applyCoverColor = (color: string | null) => {
    setCoverColor(color)
    if (color) setCoverImage(null)
    update.mutate({ coverColor: color, coverImage: color ? null : undefined })
  }

  const uploadCoverImage = async (file: File) => {
    if (!cardId) return
    setUploadingCover(true)
    try {
      const form = new FormData()
      form.append("file", file)
      const res = await fetcher<{ coverImage: string }>(`/api/cards/${cardId}/cover-image`, { method: "POST", body: form })
      setCoverImage(res.coverImage)
      setCoverColor(null)
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
    } catch (err: any) {
      toast(err.message || "Failed to upload cover image", "error")
    } finally {
      setUploadingCover(false)
    }
  }

  const removeCover = () => {
    setCoverColor(null)
    setCoverImage(null)
    update.mutate({ coverColor: null, coverImage: null })
  }

  if (!cardId) return null

  return (
    <Dialog open={open} onClose={onClose}>
      <div className="space-y-4">
        {(coverImage || coverColor) && (
          <div className="-mx-6 -mt-6 overflow-hidden rounded-t-2xl">
            {coverImage
              ? <img src={coverImage} alt="Cover" className="h-32 w-full object-cover" />
              : <div className="h-16 w-full" style={{ backgroundColor: coverColor! }} />}
          </div>
        )}
        <DialogTitle>Edit card</DialogTitle>
        <div>
          <Label>Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => title && update.mutate({ title })} />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <Label>Description</Label>
            <button type="button" onClick={() => setMdPreview((p) => !p)} className="text-xs text-muted-foreground hover:text-foreground">
              {mdPreview ? "Edit" : "Preview"}
            </button>
          </div>
          {mdPreview ? (
            <div
              className="prose prose-sm dark:prose-invert min-h-[80px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(description || "_No description_") as string }}
            />
          ) : (
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} onBlur={() => update.mutate({ description })} placeholder="Supports **markdown**" />
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Priority</Label>
            <Select
              value={priority}
              onChange={(e) => { setPriority(e.target.value); update.mutate({ priority: e.target.value }) }}
              options={[{ value: "low", label: "Low" }, { value: "medium", label: "Medium" }, { value: "high", label: "High" }, { value: "urgent", label: "Urgent" }]}
            />
          </div>
          <div>
            <Label>Assignees</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setAssigneePickerOpen((v) => !v)}
                className="flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-sm focus:outline-none"
              >
                {assigneeIds.length === 0 ? (
                  <span className="text-muted-foreground">Unassigned</span>
                ) : (
                  assigneeIds.map((uid) => {
                    const m = memberList.find((m) => m.id === uid)
                    return m ? (
                      <span key={uid} className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        <Avatar id={m.id} name={m.name} email={m.email} image={m.image} size="xs" />
                        {m.name || m.email}
                        <button type="button" onClick={(e) => { e.stopPropagation(); const next = assigneeIds.filter((id) => id !== uid); setAssigneeIds(next); update.mutate({ assigneeIds: next }) }} className="ml-0.5 hover:text-destructive">×</button>
                      </span>
                    ) : null
                  })
                )}
              </button>
              {assigneePickerOpen && (
                <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-lg border border-border bg-card shadow-lg">
                  {memberList.map((m) => {
                    const selected = assigneeIds.includes(m.id)
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          const next = selected ? assigneeIds.filter((id) => id !== m.id) : [...assigneeIds, m.id]
                          setAssigneeIds(next)
                          update.mutate({ assigneeIds: next })
                        }}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted ${selected ? "bg-primary/5" : ""}`}
                      >
                        <Avatar id={m.id} name={m.name} email={m.email} image={m.image} size="sm" />
                        <span className="flex-1 text-left">{m.name || m.email}</span>
                        {selected && <span className="text-primary">✓</span>}
                      </button>
                    )
                  })}
                  {assigneeIds.length > 0 && (
                    <button type="button" onClick={() => { setAssigneeIds([]); update.mutate({ assigneeIds: [] }); setAssigneePickerOpen(false) }} className="w-full border-t border-border px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted">
                      Clear all
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <Label>Start date</Label>
          <DatePicker value={startDate} onChange={(val) => { setStartDate(val); update.mutate({ startDate: val ? new Date(val).toISOString() : null }) }} />
        </div>
        <div>
          <Label>Due date</Label>
          <DatePicker value={dueDate} onChange={(val) => { setDueDate(val); update.mutate({ dueDate: val ? new Date(val).toISOString() : null }) }} />
        </div>

        <div>
          <Label>Story points</Label>
          <input
            type="number"
            min={0}
            max={999}
            value={storyPoints ?? ""}
            onChange={(e) => {
              const val = e.target.value === "" ? null : parseInt(e.target.value, 10)
              setStoryPoints(val)
            }}
            onBlur={() => update.mutate({ storyPoints })}
            placeholder="e.g. 3"
            className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        <div className="space-y-2 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <Label className="mb-0">Recurrence</Label>
            {!canRecur && (
              <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="inline-flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/50 px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted">
                <Lock className="h-4 w-4" />
                <span>Pro feature</span>
              </Link>
            )}
          </div>
          {canRecur && (
            <Select
              value={recurrence}
              onChange={(e) => { setRecurrence(e.target.value); update.mutate({ recurrence: e.target.value || null }) }}
              options={[
                { value: "", label: "None" },
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
              ]}
            />
          )}
        </div>

        <div className="space-y-2 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <Label className="mb-0 flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" /> Cover</Label>
            <div className="flex items-center gap-2">
              {(coverColor || coverImage) && (
                <button type="button" onClick={removeCover} className="text-xs text-muted-foreground hover:text-destructive">Remove</button>
              )}
              <button type="button" onClick={() => setShowCoverPicker((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground">
                {showCoverPicker ? "Hide" : "Change"}
              </button>
            </div>
          </div>
          {showCoverPicker && (
            <div className="space-y-2 pt-1">
              <p className="text-xs text-muted-foreground">Pick a color:</p>
              <div className="flex flex-wrap gap-1.5">
                {COVER_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => applyCoverColor(c)}
                    className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${coverColor === c ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
              {canAttach ? (
                <>
                  <p className="text-xs text-muted-foreground">Or upload an image:</p>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted">
                    <Image className="h-4 w-4" />
                    {uploadingCover ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" disabled={uploadingCover} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCoverImage(f) }} />
                  </label>
                </>
              ) : (
                <p className="text-xs text-muted-foreground"><Lock className="mr-1 inline h-3 w-3" />Cover images require Pro or Business.</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2 rounded-xl border border-border bg-card p-4">
          <Label>Labels</Label>
          <p className="text-xs text-muted-foreground">Click a label to change its color.</p>
          <div className="flex flex-wrap gap-1.5">
            {labels.map((label, i) => {
              const color = labelColors[label] ?? getDefaultColor(i)
              const isActive = activeLabel === label
              const { bg, text } = getBadgeColors(color, theme)
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setActiveLabel(isActive ? null : label)}
                  className={`flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity ${isActive ? "ring-1 ring-foreground" : ""}`}
                  style={{ backgroundColor: bg, color: text }}
                >
                  {label}
                  <span
                    onClick={(e) => { e.stopPropagation(); saveLabels(labels.filter((l) => l !== label)); if (activeLabel === label) setActiveLabel(null) }}
                    className="ml-0.5 opacity-70 hover:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </span>
                </button>
              )
            })}
          </div>
          <div className="flex gap-2">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Add label..."
              className="h-8 text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newLabel.trim() && !labels.includes(newLabel.trim())) {
                  saveLabels([...labels, newLabel.trim()])
                  setActiveLabel(newLabel.trim())
                  setNewLabel("")
                }
              }}
            />
            <Button size="sm" variant="outline" onClick={() => { if (newLabel.trim() && !labels.includes(newLabel.trim())) { saveLabels([...labels, newLabel.trim()]); setActiveLabel(newLabel.trim()); setNewLabel("") } }}>Add</Button>
          </div>
          {activeLabel && labels.includes(activeLabel) && (
            <div className="border-t border-border pt-2">
              <div className="mb-1.5 flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">Color for <span className="text-foreground">{activeLabel}</span></p>
                <button onClick={() => setActiveLabel(null)} className="text-xs text-muted-foreground hover:text-foreground">Close</button>
              </div>
              <ColorPicker
                plan={getPlan(workspace?.plan)}
                value={labelColors[activeLabel]}
                onChange={(color) => {
                  const next = { ...labelColors, [activeLabel]: color || getDefaultColor(labels.indexOf(activeLabel)) }
                  setLabelColors(next)
                  updateLabelColors.mutate(next)
                }}
                showCustom={true}
              />
            </div>
          )}
        </div>

        <div className="space-y-2 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <Label>
              Checklist
              {checklistItems.length > 0 && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">{checklistItems.filter((i) => i.checked).length}/{checklistItems.length}</span>
              )}
            </Label>
          </div>
          {checklistItems.length > 0 && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(checklistItems.filter((i) => i.checked).length / checklistItems.length) * 100}%` }} />
            </div>
          )}
          <div className="space-y-1">
            {checklistItems.map((ci) => (
              <div key={ci.id} className="group flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-muted/50">
                <input type="checkbox" checked={ci.checked} onChange={(e) => toggleChecklistItem.mutate({ id: ci.id, checked: e.target.checked })} className="h-4 w-4 cursor-pointer rounded accent-primary" />
                <span className={`flex-1 text-sm ${ci.checked ? "text-muted-foreground line-through" : ""}`}>{ci.text}</span>
                <button onClick={() => deleteChecklistItem.mutate(ci.id)} className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100" title="Remove">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newChecklistItem} onChange={(e) => setNewChecklistItem(e.target.value)} placeholder="Add item..." className="h-8 text-xs"
              onKeyDown={(e) => { if (e.key === "Enter" && newChecklistItem.trim()) addChecklistItem.mutate(newChecklistItem.trim()) }}
            />
            <Button size="sm" variant="outline" onClick={() => newChecklistItem.trim() && addChecklistItem.mutate(newChecklistItem.trim())} isLoading={addChecklistItem.isPending}>Add</Button>
          </div>
        </div>

        <div className="space-y-3 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <Label className="mb-0">Attachments</Label>
            {canAttach ? (
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-muted">
                <Upload className="h-4 w-4" />
                <span>Upload file</span>
                <input type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) addAttachment.mutate(f) }} />
              </label>
            ) : (
              <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="inline-flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/50 px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted">
                <Lock className="h-4 w-4" />
                <span>Pro feature</span>
              </Link>
            )}
          </div>
          <div className="space-y-2">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{a.fileName}</p>
                  <p className="truncate text-xs text-muted-foreground">{a.fileType || "file"} · {formatBytes(a.size)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <a href={a.fileUrl} target="_blank" rel="noreferrer" title="View" className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-xs transition-colors hover:bg-muted">
                    <Paperclip className="h-3.5 w-3.5" />
                  </a>
                  <button onClick={() => deleteAttachment.mutate(a.id)} disabled={deletingAttachment === a.id} title="Remove" className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex gap-3 border-b border-border text-sm">
            <button onClick={() => setActivityTab("comments")} className={`pb-2 font-medium transition-colors ${activityTab === "comments" ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Comments {comments.length > 0 && <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-xs">{comments.length}</span>}
            </button>
            <button onClick={() => setActivityTab("activity")} className={`pb-2 font-medium transition-colors ${activityTab === "activity" ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Activity
            </button>
          </div>

          {activityTab === "comments" ? (
            <>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {comments.length === 0 && <p className="py-2 text-center text-xs text-muted-foreground">No comments yet</p>}
                {comments.map((cmt) => (
                  <div key={cmt.id} className="group rounded-lg bg-muted/60 px-3 py-2 text-sm">
                    <div className="mb-0.5 flex items-center justify-between">
                      <span className="font-medium">{cmt.user?.name}</span>
                      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        {editingCommentId !== cmt.id && (
                          <button
                            type="button"
                            onClick={() => { setEditingCommentId(cmt.id); setEditingCommentContent(cmt.content) }}
                            className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                            title="Edit comment"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingCommentId === cmt.id ? (
                      <div className="mt-1 flex gap-2">
                        <input
                          autoFocus
                          value={editingCommentContent}
                          onChange={(e) => setEditingCommentContent(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); editComment.mutate({ id: cmt.id, content: editingCommentContent }) }
                            if (e.key === "Escape") setEditingCommentId(null)
                          }}
                          className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                        <button
                          type="button"
                          onClick={() => editComment.mutate({ id: cmt.id, content: editingCommentContent })}
                          disabled={!editingCommentContent.trim() || editComment.isPending}
                          className="rounded p-1 text-primary hover:bg-primary/10 disabled:opacity-40"
                          title="Save"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => setEditingCommentId(null)} className="rounded p-1 text-muted-foreground hover:bg-muted" title="Cancel">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-foreground/80">{cmt.content}</p>
                    )}
                    {(cmt.reactions?.length > 0 || reactionPickerFor !== cmt.id) && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {(cmt.reactions || []).reduce((acc: { emoji: string; count: number; users: string[] }[], r: any) => {
                          const ex = acc.find((a) => a.emoji === r.emoji)
                          if (ex) { ex.count++; ex.users.push(r.user?.name || r.userId) }
                          else acc.push({ emoji: r.emoji, count: 1, users: [r.user?.name || r.userId] })
                          return acc
                        }, []).map((r: { emoji: string; count: number; users: string[] }) => (
                          <button
                            key={r.emoji}
                            type="button"
                            onClick={() => toggleReaction.mutate({ commentId: cmt.id, emoji: r.emoji })}
                            className="inline-flex items-center gap-0.5 rounded-full border border-border bg-background px-1.5 py-0.5 text-xs transition-colors hover:bg-muted"
                            title={r.users.join(", ")}
                          >
                            <span>{r.emoji}</span>
                            <span className="text-muted-foreground">{r.count}</span>
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setReactionPickerFor(reactionPickerFor === cmt.id ? null : cmt.id)}
                          className="inline-flex items-center rounded-full border border-border bg-background px-1.5 py-0.5 text-muted-foreground transition-colors hover:bg-muted"
                          title="Add reaction"
                        >
                          <SmilePlus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    {reactionPickerFor === cmt.id && (
                      <div className="mt-1 flex gap-1 rounded-lg border border-border bg-card p-1.5 shadow-sm">
                        {["👍", "❤️", "🎉", "😂", "🚀", "👀", "🔥", "✅"].map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => toggleReaction.mutate({ commentId: cmt.id, emoji })}
                            className="rounded p-1 text-lg transition-transform hover:scale-125 hover:bg-muted"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="relative mt-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      ref={commentRef}
                      value={comment}
                      onChange={(e) => {
                        const val = e.target.value
                        setComment(val)
                        const atIdx = val.lastIndexOf("@")
                        if (atIdx !== -1) {
                          const after = val.slice(atIdx + 1)
                          if (!after.includes(" ")) { setMentionQuery(after); setMentionIndex(0) }
                          else setMentionQuery(null)
                        } else { setMentionQuery(null) }
                      }}
                      onKeyDown={(e) => {
                        const filtered = mentionQuery !== null ? memberList.filter((m) => (m.name || m.email).toLowerCase().includes(mentionQuery.toLowerCase())) : []
                        if (mentionQuery !== null && filtered.length > 0) {
                          if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => (i + 1) % filtered.length) }
                          if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((i) => (i - 1 + filtered.length) % filtered.length) }
                          if (e.key === "Enter" || e.key === "Tab") {
                            e.preventDefault()
                            const m = filtered[mentionIndex]
                            const atIdx = comment.lastIndexOf("@")
                            setComment(comment.slice(0, atIdx) + `@${m.name || m.email} `)
                            setMentionQuery(null)
                          }
                          if (e.key === "Escape") setMentionQuery(null)
                        }
                      }}
                      placeholder="Add a comment… use @ to mention"
                    />
                    {mentionQuery !== null && (() => {
                      const filtered = memberList.filter((m) => (m.name || m.email).toLowerCase().includes(mentionQuery.toLowerCase()))
                      return filtered.length > 0 ? (
                        <div className="absolute bottom-full left-0 z-50 mb-1 w-56 overflow-hidden rounded-lg border border-border bg-card shadow-lg">
                          {filtered.map((m, i) => (
                            <button key={m.id} type="button"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                const atIdx = comment.lastIndexOf("@")
                                setComment(comment.slice(0, atIdx) + `@${m.name || m.email} `)
                                setMentionQuery(null)
                              }}
                              className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted ${i === mentionIndex ? "bg-muted" : ""}`}
                            >
                              <Avatar id={m.id} name={m.name} email={m.email} image={m.image} size="sm" />
                              {m.name || m.email}
                            </button>
                          ))}
                        </div>
                      ) : null
                    })()}
                  </div>
                  <Button onClick={() => { addComment.mutate(comment); setMentionQuery(null) }} disabled={!comment.trim()} isLoading={addComment.isPending}>Post</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="max-h-60 space-y-1 overflow-y-auto">
              {activityLogs.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">No activity yet</p>}
              {activityLogs.map((log) => {
                const who = log.user?.name || log.user?.email || "Someone"
                const when = new Date(log.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                let text = ""
                if (log.type === "created") text = `created this card`
                else if (log.type === "title") text = `renamed to "${log.to}"`
                else if (log.type === "priority") text = `changed priority from ${log.from} to ${log.to}`
                else if (log.type === "assigned") text = log.to ? `assigned to ${log.to}` : `removed assignee`
                else if (log.type === "moved") text = `moved card to a new column`
                else if (log.type === "dueDate") text = log.to ? `set due date to ${new Date(log.to).toLocaleDateString()}` : `cleared due date`
                else if (log.type === "label") text = `updated labels`
                else if (log.type === "comment") text = `commented: ${log.to}`
                else if (log.type === "checklist") text = log.to ? `added checklist item "${log.to}"` : `removed checklist item`
                return (
                  <div key={log.id} className="flex items-start gap-2 rounded-md px-1 py-1.5 text-xs">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                      {who[0].toUpperCase()}
                    </div>
                    <div className="flex-1 text-muted-foreground">
                      <span className="font-medium text-foreground">{who}</span> {text}
                      <span className="ml-1.5 text-[10px]">{when}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3">
          {confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Permanently delete?</span>
              <Button variant="destructive" size="sm" onClick={() => deleteCard.mutate()} isLoading={deleteCard.isPending}>Yes, delete</Button>
              <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            </div>
          ) : confirmArchive ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Archive card?</span>
              <Button variant="outline" size="sm" onClick={() => archiveCard.mutate()} isLoading={archiveCard.isPending}>Yes, archive</Button>
              <Button variant="outline" size="sm" onClick={() => setConfirmArchive(false)}>Cancel</Button>
            </div>
          ) : isArchived ? (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="text-primary hover:bg-primary/10" onClick={() => unarchiveCard.mutate()} isLoading={unarchiveCard.isPending}>
                <ArchiveRestore className="mr-1.5 h-4 w-4" /> Restore
              </Button>
              <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="mr-1.5 h-4 w-4" /> Delete forever
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:bg-muted" onClick={() => setConfirmArchive(true)}>
                <Archive className="mr-1.5 h-4 w-4" /> Archive
              </Button>
              <Button variant="ghost" size="sm" onClick={() => duplicateCard.mutate()} isLoading={duplicateCard.isPending}>
                <Copy className="mr-1.5 h-4 w-4" /> Duplicate
              </Button>
              <Button variant="ghost" size="sm" onClick={() => toggleSubscribe.mutate()} isLoading={toggleSubscribe.isPending} title={subscribed ? "Unfollow this card" : "Follow this card"}>
                {subscribed ? <><BellOff className="mr-1.5 h-4 w-4" /> Unfollow</> : <><Bell className="mr-1.5 h-4 w-4" /> Follow</>}
              </Button>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Dialog>
  )
}
